import React, { useEffect, useState } from 'react';
import { Alert, Card, Table, Button, Form, Modal, Tabs, Tab, Badge, Row, Col, ProgressBar } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { faArrowLeft, faDownload, faEnvelope, faEye, faTrash, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import ActionIconButton from '../../components/ActionIconButton';
import {
    getClientDetails,
    addClientProject,
    addClientInvoice,
    addClientActivity,
    assignClientProjectStaff,
    deleteClientDocument,
    getClientProjectAssignments,
    getStaffListLatest,
    uploadClientDocument,
    sendClientInvoiceEmail,
} from '../../service/apiService';

function ClientDetails() {
    const { clientId } = useParams();
    const navigate = useNavigate();

    const [client, setClient] = useState(null);
    const [projects, setProjects] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [activities, setActivities] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [message, setMessage] = useState('');
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
const [selectedInvoice, setSelectedInvoice] = useState(null);
const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });
const [sendingEmail, setSendingEmail] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const emptyInvoiceForm = {
        project_id: '',
        status: 'unpaid',
        discount: '',
        items: [{ service: '', amount: '' }],
    };
    const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm);
    const [documentForm, setDocumentForm] = useState({
        title: '',
        document_type: 'agreement',
        project_id: '',
        file: null,
    });
    const [form, setForm] = useState({});

    const assignmentStatusBadge = (status) => {
        const variants = {
            assigned: 'secondary',
            in_progress: 'primary',
            stuck: 'danger',
            completed: 'success',
        };
        const label = (status || 'assigned').replaceAll('_', ' ');
        return <Badge bg={variants[status] || 'secondary'}>{label}</Badge>;
    };

    const loadClient = async () => {
        const data = await getClientDetails(clientId);
        setClient(data.client);
        setProjects(data.projects || []);
        setInvoices(data.invoices || []);
        setActivities(data.activities || []);
        setDocuments(data.documents || []);
    };

    useEffect(() => {
        loadClient();
    }, [clientId]);

    useEffect(() => {
        const loadStaff = async () => {
            const res = await getStaffListLatest();
            setStaffList(res.data || []);
        };
        loadStaff();
    }, []);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const openInvoiceModal = () => {
        setInvoiceForm(emptyInvoiceForm);
        setShowInvoiceModal(true);
    };

    const updateInvoiceItem = (index, key, value) => {
        setInvoiceForm((prev) => ({
            ...prev,
            items: prev.items.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
        }));
    };

    const addInvoiceItem = () => {
        setInvoiceForm((prev) => ({
            ...prev,
            items: [...prev.items, { service: '', amount: '' }],
        }));
    };

    const removeInvoiceItem = (index) => {
        setInvoiceForm((prev) => ({
            ...prev,
            items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== index) : prev.items,
        }));
    };

    const invoiceItems = invoiceForm.items
        .map((item) => ({
            service: item.service.trim(),
            amount: Number(item.amount || 0),
        }))
        .filter((item) => item.service);

    const invoiceSubtotal = invoiceItems.reduce((sum, item) => sum + item.amount, 0);
    const invoiceDiscount = Number(invoiceForm.discount || 0);
    const invoicePayable = Math.max(0, invoiceSubtotal - invoiceDiscount);

    const handleAddProject = async (e) => {
        e.preventDefault();
        const res = await addClientProject({ client_id: client.id, ...form });
        if (res.status === 'success') {
            setMessage('Project added');
            setShowProjectModal(false);
            setForm({});
            loadClient();
        } else {
            setMessage(res.message || 'Unable to add project');
        }
    };

    const handleAddInvoice = async (e) => {
        e.preventDefault();
        if (!invoiceItems.length) {
            setMessage('Please add at least one service');
            return;
        }

        const res = await addClientInvoice({
            client_id: client.id,
            project_id: invoiceForm.project_id || 0,
            status: invoiceForm.status,
            discount: invoiceDiscount,
            invoice_items: invoiceItems,
            amount: invoicePayable,
            description: invoiceItems.map((item) => item.service).join('\n'),
        });
        if (res.status === 'success') {
            setMessage('Invoice generated');
            setShowInvoiceModal(false);
            setInvoiceForm(emptyInvoiceForm);
            loadClient();
        } else {
            setMessage(res.message || 'Unable to generate invoice');
        }
    };

    const handleAddActivity = async (e) => {
        e.preventDefault();
        const res = await addClientActivity({ client_id: client.id, ...form });
        if (res.status === 'success') {
            setMessage('Activity logged');
            setShowActivityModal(false);
            setForm({});
            loadClient();
        } else {
            setMessage(res.message || 'Unable to log activity');
        }
    };

    const openAssignModal = async (project) => {
        setSelectedProject(project);
        const res = await getClientProjectAssignments(project.id);
        const rows = res.status === 'success' ? res.data : [];
        setAssignments(rows.length ? rows.map((row) => ({
            staff_id: String(row.staff_id),
            role: row.role || 'Developer',
            work_status: row.work_status || 'assigned',
        })) : [{ staff_id: '', role: 'Developer' }]);
        setShowAssignModal(true);
    };

    const saveAssignments = async (e) => {
        e.preventDefault();
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const res = await assignClientProjectStaff({
            project_id: selectedProject.id,
            assigned_by: currentUser.id || 0,
            assignments: assignments.filter((item) => item.staff_id),
        });

        if (res.status === 'success') {
            setMessage('Project assignments updated');
            setShowAssignModal(false);
            loadClient();
        } else {
            setMessage(res.message || 'Unable to update assignments');
        }
    };

    const handleUploadDocument = async (e) => {
        e.preventDefault();
        if (!documentForm.file) {
            setMessage('Please select a document');
            return;
        }

        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const fd = new FormData();
        fd.append('client_id', client.id);
        fd.append('project_id', documentForm.project_id || '');
        fd.append('document_type', documentForm.document_type);
        fd.append('title', documentForm.title);
        fd.append('uploaded_by', currentUser.id || 0);
        fd.append('document', documentForm.file);

        const res = await uploadClientDocument(fd);
        if (res.status === 'success') {
            setMessage('Document uploaded');
            setShowDocumentModal(false);
            setDocumentForm({ title: '', document_type: 'agreement', project_id: '', file: null });
            loadClient();
        } else {
            setMessage(res.message || 'Unable to upload document');
        }
    };

    const handleDeleteDocument = async (id) => {
        if (!window.confirm('Delete this document?')) return;
        const res = await deleteClientDocument(id);
        if (res.status === 'success') {
            setMessage('Document deleted');
            loadClient();
        } else {
            setMessage(res.message || 'Unable to delete document');
        }
    };
    const openEmailModal = (invoice) => {
    setSelectedInvoice(invoice);
    setEmailForm({
        to: client.email || '',
        subject: `Invoice ${invoice.invoice_no} - ${client.client_name}`,
        body: `Dear ${client.contact_person || client.client_name},\n\nPlease find attached invoice ${invoice.invoice_no} for ₹${invoice.amount}.\n\nRegards,\nTeam`,
    });
    setShowEmailModal(true);
};

const handleSendInvoiceEmail = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    try {
        const res = await sendClientInvoiceEmail({
            invoice_id: selectedInvoice.id,
            client_id: client.id,
            to: emailForm.to,
            subject: emailForm.subject,
            body: emailForm.body,
        });
        if (res.status === 'success') {
            setMessage('Invoice emailed successfully');
            setShowEmailModal(false);
        } else {
            setMessage(res.message || 'Unable to send invoice email');
        }
    } catch (err) {
        setMessage('Unable to send invoice email');
    } finally {
        setSendingEmail(false);
    }
};

    if (!client) return <p>Loading...</p>;

    return (
        <div className="client-details-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h3 className="mb-1">{client.client_name}</h3>
                    <div className="text-muted small">
                        {client.client_code || `CL${String(client.id).padStart(4, '0')}`} | {client.business_name || client.client_type?.replace('_', ' ')}
                    </div>
                </div>
                <ActionIconButton variant="outline-secondary" icon={faArrowLeft} label="Back" onClick={() => navigate(-1)} />
            </div>

            {message && <Alert variant="info" onClose={() => setMessage('')} dismissible>{message}</Alert>}

            <Card className="shadow-sm p-3 mb-3">
                <h5>Client Overview</h5>
                <p><strong>Type:</strong> {client.client_type.replace('_', ' ')}</p>
                <p><strong>Contact:</strong> {client.contact_person || '-'} | {client.phone || '-'} | {client.email}</p>
                <p><strong>Business:</strong> {client.business_name || '-'} {client.website ? `| ${client.website}` : ''}</p>
                <p><strong>Address:</strong> {client.address || '-'}, {client.city || '-'}</p>
                <p><strong>Notes:</strong> {client.notes || '-'}</p>
                <Badge bg={client.status === 'active' ? 'success' : 'secondary'}>{client.status || 'active'}</Badge>
            </Card>

            <Row className="g-3 mb-3">
                <Col md={4}>
                    <Card className="shadow-sm"><Card.Body><div className="text-muted small">Projects</div><h4 className="mb-0">{projects.length}</h4></Card.Body></Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm"><Card.Body><div className="text-muted small">Documents</div><h4 className="mb-0">{documents.length}</h4></Card.Body></Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <div className="d-flex justify-content-between small text-muted">
                                <span>Completed</span>
                                <span>{projects.length ? Math.round((projects.filter((p) => p.status === 'completed').length / projects.length) * 100) : 0}%</span>
                            </div>
                            <ProgressBar now={projects.length ? (projects.filter((p) => p.status === 'completed').length / projects.length) * 100 : 0} className="mt-2" />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Tabs defaultActiveKey="projects" className="mb-3">
                {/* Projects */}
                <Tab eventKey="projects" title="Projects">
                    <div className="d-flex justify-content-end mb-3">
                        <Button size="sm" onClick={() => setShowProjectModal(true)}>+ Add Project</Button>
                    </div>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Budget</th>
                                <th>Assigned Staff</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.length > 0 ? projects.map((p) => (
                                <tr key={p.id}>
                                    <td>{p.project_name}</td>
                                    <td><Badge bg={p.status === 'completed' ? 'success' : 'warning'}>{p.status}</Badge></td>
                                    <td>₹ {p.budget}</td>
                                    <td>{p.assigned_staff || '-'}</td>
                                    <td>{p.start_date || '-'}</td>
                                    <td>{p.end_date || '-'}</td>
                                    <td><ActionIconButton size="sm" variant="outline-primary" icon={faUserPlus} label="Assign staff" onClick={() => openAssignModal(p)} /></td>
                                </tr>
                            )) : <tr><td colSpan="7" className="text-center text-muted">No projects found</td></tr>}
                        </tbody>
                    </Table>
                </Tab>

                <Tab eventKey="documents" title="Documents">
                    <div className="d-flex justify-content-end mb-3">
                        <Button size="sm" onClick={() => setShowDocumentModal(true)}>+ Upload Document</Button>
                    </div>
                    <Table bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Type</th>
                                <th>Project</th>
                                <th>Uploaded</th>
                                <th>File</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {documents.length > 0 ? documents.map((doc) => (
                                <tr key={doc.id}>
                                    <td>{doc.title}</td>
                                    <td><Badge bg="secondary">{doc.document_type}</Badge></td>
                                    <td>{doc.project_name || '-'}</td>
                                    <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '-'}</td>
                                    <td><a href={`http://localhost/crm/api/${doc.file_path}`} target="_blank" rel="noreferrer">{doc.file_name}</a></td>
                                    <td><ActionIconButton size="sm" variant="outline-danger" icon={faTrash} label="Delete document" onClick={() => handleDeleteDocument(doc.id)} /></td>
                                </tr>
                            )) : <tr><td colSpan="6" className="text-center text-muted">No documents found</td></tr>}
                        </tbody>
                    </Table>
                </Tab>

                {/* Invoices */}
                <Tab eventKey="invoices" title="Invoices">
                    <div className="d-flex justify-content-end mb-3">
                        <Button size="sm" onClick={openInvoiceModal}>+ Generate Invoice</Button>
                    </div>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>No</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length > 0 ? (
                                invoices.map((inv) => (
                                    <tr key={inv.id}>
                                        <td>{inv.invoice_no}</td>
                                        <td>₹ {inv.amount}</td>
                                        <td>
                                            <Badge bg={inv.status === 'paid' ? 'success' : 'danger'}>
                                                {inv.status}
                                            </Badge>
                                        </td>
                                        <td>{inv.date}</td>
                                        <td>
                                            {inv.pdf_path ? (
                                                <div className="erp-action-row">
                                                    <ActionIconButton
                                                        as="a"
                                                        size="sm"
                                                        variant="outline-primary"
                                                        icon={faEye}
                                                        label="View invoice"
                                                        href={`http://localhost/crm/api/${inv.pdf_path}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    />
                                                    <ActionIconButton
                                                        as="a"
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        icon={faDownload}
                                                        label="Download invoice"
                                                        href={`http://localhost/crm/api/${inv.pdf_path}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        download
                                                    />
                                                    <ActionIconButton
                                                        size="sm"
                                                        variant="outline-success"
                                                        icon={faEnvelope}
                                                        label="Email invoice"
                                                        onClick={() => openEmailModal(inv)}
                                                    />
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted">
                                        No invoices found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                </Tab>

                {/* Activities */}
                <Tab eventKey="activities" title="Activities">
                    <div className="d-flex justify-content-end mb-3">
                        <Button size="sm" onClick={() => setShowActivityModal(true)}>+ Log Activity</Button>
                    </div>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activities.length > 0 ? activities.map((a) => (
                                <tr key={a.id}>
                                    <td>{a.activity_type}</td>
                                    <td>{a.description}</td>
                                    <td>{new Date(a.created_on).toLocaleString()}</td>
                                </tr>
                            )) : <tr><td colSpan="3" className="text-center text-muted">No activities found</td></tr>}
                        </tbody>
                    </Table>
                </Tab>
            </Tabs>

            {/* Modals for adding new entries */}
            <Modal show={showProjectModal} onHide={() => setShowProjectModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Add Project</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddProject}>
                        <Form.Control name="project_name" placeholder="Project Name" onChange={handleChange} required className="mb-2" />
                        <Form.Control name="description" placeholder="Description" as="textarea" onChange={handleChange} className="mb-2" />
                        <Row>
                            <Col md={6}><Form.Control name="start_date" type="date" onChange={handleChange} className="mb-2" /></Col>
                            <Col md={6}><Form.Control name="end_date" type="date" onChange={handleChange} className="mb-2" /></Col>
                        </Row>
                        <Form.Select name="status" defaultValue="ongoing" onChange={handleChange} className="mb-2">
                            <option value="ongoing">Ongoing</option>
                            <option value="completed">Completed</option>
                            <option value="on_hold">On Hold</option>
                        </Form.Select>
                        <Form.Control name="budget" placeholder="Budget" type="number" onChange={handleChange} className="mb-2" />
                        <Form.Control name="remarks" placeholder="Remarks" onChange={handleChange} className="mb-2" />
                        <Button type="submit" variant="primary">Save</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showInvoiceModal} onHide={() => setShowInvoiceModal(false)} centered size="lg">
                <Modal.Header closeButton><Modal.Title>Generate Invoice</Modal.Title></Modal.Header>
                <Form onSubmit={handleAddInvoice}>
                    <Modal.Body>
                        <Row className="g-2 mb-3">
                            <Col md={7}>
                                <Form.Label>Project</Form.Label>
                                <Form.Select value={invoiceForm.project_id} onChange={(e) => setInvoiceForm({ ...invoiceForm, project_id: e.target.value })}>
                                    <option value="">General client invoice</option>
                                    {projects.map((project) => <option key={project.id} value={project.id}>{project.project_name}</option>)}
                                </Form.Select>
                            </Col>
                            <Col md={5}>
                                <Form.Label>Status</Form.Label>
                                <Form.Select value={invoiceForm.status} onChange={(e) => setInvoiceForm({ ...invoiceForm, status: e.target.value })}>
                                    <option value="unpaid">Unpaid</option>
                                    <option value="paid">Paid</option>
                                    <option value="partial">Partial</option>
                                </Form.Select>
                            </Col>
                        </Row>

                        <div className="border rounded p-3 mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">Services</h6>
                                <Button type="button" variant="outline-primary" size="sm" onClick={addInvoiceItem}>+ Add Service</Button>
                            </div>
                            <Table bordered responsive className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: '70px' }}>SL</th>
                                        <th>Service</th>
                                        <th style={{ width: '180px' }}>Amount</th>
                                        <th style={{ width: '70px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceForm.items.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <Form.Control
                                                    value={item.service}
                                                    placeholder="Service name"
                                                    onChange={(e) => updateInvoiceItem(index, 'service', e.target.value)}
                                                    required={index === 0}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    value={item.amount}
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    onChange={(e) => updateInvoiceItem(index, 'amount', e.target.value)}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <Button type="button" variant="outline-danger" size="sm" onClick={() => removeInvoiceItem(index)} disabled={invoiceForm.items.length === 1}>x</Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {invoiceForm.items.length > 5 && <div className="text-muted small mt-2">PDF will continue on the next page after every 5 service rows.</div>}
                        </div>

                        <Row className="g-2 justify-content-end">
                            <Col md={4}>
                                <Form.Label>Total</Form.Label>
                                <Form.Control value={invoiceSubtotal.toFixed(2)} readOnly />
                            </Col>
                            <Col md={4}>
                                <Form.Label>Discount</Form.Label>
                                <Form.Control
                                    value={invoiceForm.discount}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    onChange={(e) => setInvoiceForm({ ...invoiceForm, discount: e.target.value })}
                                />
                            </Col>
                            <Col md={4}>
                                <Form.Label>Total Payable Amount</Form.Label>
                                <Form.Control value={invoicePayable.toFixed(2)} readOnly />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary">Generate</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showActivityModal} onHide={() => setShowActivityModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Log Activity</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddActivity}>
                        <Form.Select name="activity_type" onChange={handleChange} className="mb-2">
                            <option value="call">Call</option>
                            <option value="meeting">Meeting</option>
                            <option value="email">Email</option>
                            <option value="followup">Follow-up</option>
                        </Form.Select>
                        <Form.Control name="description" placeholder="Description" as="textarea" onChange={handleChange} required className="mb-2" />
                        <Button type="submit" variant="primary">Save</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered size="lg">
                <Modal.Header closeButton><Modal.Title>Assign Staff - {selectedProject?.project_name}</Modal.Title></Modal.Header>
                <Form onSubmit={saveAssignments}>
                    <Modal.Body>
                        {assignments.map((item, index) => (
                            <Row className="g-2 mb-2" key={index}>
                                <Col md={5}>
                                    <Form.Select
                                        value={item.staff_id}
                                        onChange={(e) => setAssignments((prev) => prev.map((row, i) => i === index ? { ...row, staff_id: e.target.value } : row))}
                                        required
                                    >
                                        <option value="">Select Staff</option>
                                        {staffList.map((staff) => <option key={staff.id} value={staff.id}>{staff.name}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select
                                        value={item.role}
                                        onChange={(e) => setAssignments((prev) => prev.map((row, i) => i === index ? { ...row, role: e.target.value } : row))}
                                    >
                                        <option>Project Manager</option>
                                        <option>Developer</option>
                                        <option>Designer</option>
                                        <option>Tester</option>
                                        <option>Support</option>
                                        <option>Coordinator</option>
                                        <option>Trainer</option>
                                        <option>Consultant</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3} className="d-flex align-items-center">
                                    {assignmentStatusBadge(item.work_status)}
                                </Col>
                                <Col md={1}>
                                    <ActionIconButton variant="outline-danger" icon={faTrash} label="Remove staff" onClick={() => setAssignments((prev) => prev.filter((_, i) => i !== index))} />
                                </Col>
                            </Row>
                        ))}
                        <Button variant="outline-secondary" size="sm" onClick={() => setAssignments([...assignments, { staff_id: '', role: 'Developer', work_status: 'assigned' }])}>+ Add Staff</Button>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                        <Button type="submit">Save Assignments</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showDocumentModal} onHide={() => setShowDocumentModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Upload Client Document</Modal.Title></Modal.Header>
                <Form onSubmit={handleUploadDocument}>
                    <Modal.Body>
                        <Form.Label>Title</Form.Label>
                        <Form.Control value={documentForm.title} onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })} required className="mb-2" />
                        <Form.Label>Type</Form.Label>
                        <Form.Select value={documentForm.document_type} onChange={(e) => setDocumentForm({ ...documentForm, document_type: e.target.value })} className="mb-2">
                            <option value="agreement">Agreement</option>
                            <option value="quotation">Quotation</option>
                            <option value="invoice">Invoice</option>
                            <option value="logo">Logo</option>
                            <option value="proposal">Proposal</option>
                            <option value="other">Other</option>
                        </Form.Select>
                        <Form.Label>Project</Form.Label>
                        <Form.Select value={documentForm.project_id} onChange={(e) => setDocumentForm({ ...documentForm, project_id: e.target.value })} className="mb-2">
                            <option value="">General client document</option>
                            {projects.map((project) => <option key={project.id} value={project.id}>{project.project_name}</option>)}
                        </Form.Select>
                        <Form.Label>File</Form.Label>
                        <Form.Control type="file" onChange={(e) => setDocumentForm({ ...documentForm, file: e.target.files[0] })} required />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDocumentModal(false)}>Cancel</Button>
                        <Button type="submit">Upload</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered>
    <Modal.Header closeButton>
        <Modal.Title>Email Invoice {selectedInvoice?.invoice_no}</Modal.Title>
    </Modal.Header>
    <Form onSubmit={handleSendInvoiceEmail}>
        <Modal.Body>
            <Form.Label>To</Form.Label>
            <Form.Control
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                required
                className="mb-2"
            />
            <Form.Label>Subject</Form.Label>
            <Form.Control
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                required
                className="mb-2"
            />
            <Form.Label>Message</Form.Label>
            <Form.Control
                as="textarea"
                rows={5}
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                required
                className="mb-2"
            />
            <div className="text-muted small">
                The invoice PDF ({selectedInvoice?.pdf_path?.split('/').pop()}) will be attached automatically.
            </div>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={sendingEmail}>
                {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
        </Modal.Footer>
    </Form>
</Modal>

        </div>
    );
}

export default ClientDetails;

